
























































































































































































































































import { Vue, Component, Prop, Watch } from "@/shared/vuepack";
import Button from "@/shared/components/ui/Button.vue";
import { useThemeStore } from "@/shared/store/theme";
import { Beneficiary, BeneficiaryRelationshipType, BeneficiaryStatus, BeneficiaryType, Address } from "@/shared/types";
import UsStateSelector from "@/shared/components/UsStateSelector.vue";
import _ from "lodash";
import AddressForm from "@/employee/components/settings/profile/AddressForm.vue";
import { useAddressFormStore } from "@/employee/store/addressForm";

const emptyAddress: Address = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: ""
};

@Component({
  components: {
    Button,
    UsStateSelector,
    AddressForm
  }
})
export default class EditBeneficiary extends Vue {
  @Prop ({ required: true })
  beneficiary: Beneficiary;

  @Prop ({ required: false })
  currentBeneficiaries: Beneficiary[];

  @Prop ({ required: true })
  onSave: () => Promise<void>;

  @Prop ()
  toggleClose: () => void;

  @Prop ()
  isNew: boolean;

  @Prop ()
  show: { value: boolean };

  @Prop ({ required: false })
  hasStatusChanged: { value: boolean };

  addressFormStore = useAddressFormStore();
  themeStore = useThemeStore();

  isSubmitting = false;
  needSharePercentageAdjustment = false;
  addressModel = _.cloneDeep(emptyAddress);
  BeneficiaryRelationshipType = BeneficiaryRelationshipType;
  BeneficiaryType = BeneficiaryType;
  isModalScrollable = false;

  spouseCannotBeContingent = {
    getMessage () {
      return "Spouse cannot be contingent beneficiary.";
    },
    validate (beneficiaryType, args) {
      const beneficiaryRelationshipType = args[0];

      if (beneficiaryRelationshipType === BeneficiaryRelationshipType.Spouse && beneficiaryType === BeneficiaryType.Contingent) {
        return false;
      }

      return true;
    }
  };

  mounted () {
    this.setModalScrollable();
    window.addEventListener("resize", this.setModalScrollable);
    this.$validator.extend("spouseCannotBeContingent", this.spouseCannotBeContingent);
  }

  @Watch("beneficiary")
  onBeneficiaryChanged () {
    this.populateAddressModelFromBeneficiary();
  }

  get isShareEditFormVisible () {
    return this.needSharePercentageAdjustment || this.hasStatusChanged;
  }

  get statusForDisplay () {
    if (!this.hasStatusChanged) {
      return "";
    }

    return this.beneficiary.status === BeneficiaryStatus.Active ? "activating" : "deactivating";
  }

  get title () {
    return this.isNew ? "Add Beneficiary"
      : this.isShareEditFormVisible
        ? "Update Beneficiary Shares"
        : "Update Beneficiary";
  }

  get submitDisabled () {
    return !this.isValid || this.isSubmitting || this.addressFormStore.saveDisabled;
  }

  get isValid () {
    return !this.$validator.errors.any() && this.addressFormStore.isValid;
  }

  get hasMultipleBeneficiaries () {
    return this.currentBeneficiaries.length > 0;
  }

  get primaryBeneficiaries () {
    const all = this.currentBeneficiaries.filter(b => b.type === BeneficiaryType.Primary);
    if (this.beneficiary.type === BeneficiaryType.Primary) {
      all.push(this.beneficiary);
    }

    return all;
  }

  get contingentBeneficiaries () {
    const all = this.currentBeneficiaries.filter(b => b.type === BeneficiaryType.Contingent);
    if (this.beneficiary.type === BeneficiaryType.Contingent) {
      all.push(this.beneficiary);
    }

    return all;
  }

  get totalPrimarySharePercentage () {
    return _.sumBy(this.primaryBeneficiaries, x => x.share);
  }

  get totalContingentSharePercentage () {
    return _.sumBy(this.contingentBeneficiaries, x => x.share);
  }

  get canGoBack () {
    return this.needSharePercentageAdjustment && !this.hasStatusChanged;
  }

  populateAddressModelFromBeneficiary () {
    if (this.beneficiary) {
      this.addressModel = {
        addressLine1: this.beneficiary.address1,
        addressLine2: this.beneficiary.address2,
        city: this.beneficiary.city,
        state: this.beneficiary.stateCode,
        zip: this.beneficiary.zip
      };
    } else {
      this.addressModel = _.cloneDeep(emptyAddress);
    }
  }

  goBack () {
    this.needSharePercentageAdjustment = false;
  }

  isShareTotalValid (type: BeneficiaryType): boolean {
    switch (type) {
      case BeneficiaryType.Primary:
        return this.totalPrimarySharePercentage > 0 && this.totalPrimarySharePercentage !== 100;
      case BeneficiaryType.Contingent:
        return this.totalContingentSharePercentage > 0 && this.totalContingentSharePercentage !== 100;
    }
  }

  save () {
    this.copyAddressModelToBeneficiary();

    this.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.verifyAddress()
        .then(() => {
          if (this.isShareTotalValid(BeneficiaryType.Primary) || this.isShareTotalValid(BeneficiaryType.Contingent)) {
            if (this.hasMultipleBeneficiaries) {
              this.needSharePercentageAdjustment = true;
            } else {
              this.errors.add({field: "share", msg: "Value must be 100 for a single beneficiary."});
            }

            return;
          }

          this.isSubmitting = true;

          this.onSave().finally(() => {
            this.isSubmitting = false;
            this.needSharePercentageAdjustment = false;
          });
        })
        .catch(() => {/*noop*/});
    });
  }

  verifyAddress (): Promise<void> {
    const addressForm = this.getAddressComponent();

    if (addressForm) {
      return addressForm.verifyAddress();
    }

    return Promise.resolve();
  }

  copyAddressModelToBeneficiary () {
    this.beneficiary.address1 = this.addressModel.addressLine1;
    this.beneficiary.address2 = this.addressModel.addressLine2;
    this.beneficiary.city = this.addressModel.city;
    this.beneficiary.stateCode = this.addressModel.state;
    this.beneficiary.zip = this.addressModel.zip;
  }

  validateAll () {
    const validations = [
      this.$validator.validateAll(),
      this.validateAddress()
    ];

    return Promise.all(validations).then(results => {
      return results.every(result => result === true);
    });
  }

  validateAddress (): Promise<boolean> {
    const addressForm = this.getAddressComponent();

    if (addressForm) {
      return addressForm.validate();
    }

    return Promise.resolve(true);
  }

  isInactive (status: BeneficiaryStatus): boolean {
    return status === BeneficiaryStatus.Inactive;
  }

  onClose () {
    this.needSharePercentageAdjustment = false;
    this.addressFormStore.resetState();
    this.toggleClose();
  }

  getAddressComponent () {
    return this.$refs.addressForm as any;
  }

  setModalScrollable () {
    const smallScreenMaxBreakpoint = 768;
    this.isModalScrollable = window.innerWidth < smallScreenMaxBreakpoint;
  }

  beforeDestroy () {
    window.removeEventListener("resize", this.setModalScrollable);
  }
}
